import { useState } from 'react';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F';
type MomentumStatus = 'improving' | 'stable' | 'declining';

interface SecurityCultureScore {
  id: string;
  total_score: number;
  letter_grade: LetterGrade;
  training_score: number;
  phishing_score: number;
  email_score: number;
  proactive_score: number;
  training_completion_rate: number;
  training_overdue_rate: number;
  phishing_click_rate: number;
  phishing_report_rate: number;
  dmarc_enforcement_level: number;
  spf_dkim_pass_rate: number;
  blacklist_clean: boolean;
  dark_web_response_rate: number;
  shadow_ai_remediation_rate: number;
  posture_resolution_rate: number;
  score_4w_ago?: number;
  score_8w_ago?: number;
  score_12w_ago?: number;
  momentum: MomentumStatus;
  consecutive_improving_weeks: number;
  is_security_champion: boolean;
  computed_at: string;
  period_start: string;
  period_end: string;
}

interface SecurityCultureCertificate {
  id: string;
  score_id: string;
  total_score: number;
  letter_grade: LetterGrade;
  org_name: string;
  valid_from: string;
  valid_until: string;
  certificate_number: string;
  is_revoked: boolean;
}

interface CurrentScoreResponse {
  data: SecurityCultureScore | null;
}

interface HistoryResponse {
  data: SecurityCultureScore[];
}

interface CertificateResponse {
  data: SecurityCultureCertificate | null;
}

interface LeaderboardResponse {
  data: {
    your_score: number | null;
    your_grade: LetterGrade | null;
    industry_average: number;
    smb_average: number;
    percentile: number | null;
    note: string;
  };
}

interface ComputeResponse {
  data: SecurityCultureScore;
  certificate: SecurityCultureCertificate | null;
}

const GRADE_COLORS: Record<LetterGrade, string> = {
  A: 'text-emerald-600',
  B: 'text-green-500',
  C: 'text-yellow-500',
  D: 'text-orange-500',
  F: 'text-red-600',
};

const GRADE_BG_COLORS: Record<LetterGrade, string> = {
  A: 'bg-emerald-50 border-emerald-200',
  B: 'bg-green-50 border-green-200',
  C: 'bg-yellow-50 border-yellow-200',
  D: 'bg-orange-50 border-orange-200',
  F: 'bg-red-50 border-red-200',
};

const GRADE_RING: Record<LetterGrade, string> = {
  A: 'ring-emerald-500',
  B: 'ring-green-500',
  C: 'ring-yellow-500',
  D: 'ring-orange-500',
  F: 'ring-red-600',
};

function pct(val: number) {
  return `${Math.round(val * 100)}%`;
}

function ProgressBar({ value, max = 25, color = 'bg-emerald-500' }: { value: number; max?: number; color?: string }) {
  const width = Math.round((value / max) * 100);
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function ScoreHistoryChart({ scores }: { scores: SecurityCultureScore[] }) {
  if (scores.length === 0) return null;

  const sorted = [...scores].reverse(); // oldest first
  const maxScore = 100;
  const width = 500;
  const height = 120;
  const padL = 32;
  const padR = 12;
  const padT = 12;
  const padB = 24;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;

  function xPos(i: number) {
    return padL + (sorted.length > 1 ? (i / (sorted.length - 1)) * chartW : chartW / 2);
  }
  function yPos(score: number) {
    return padT + chartH - (score / maxScore) * chartH;
  }

  const points = sorted.map((s, i) => `${xPos(i)},${yPos(s.total_score)}`).join(' ');

  // Grade threshold lines
  const thresholds: Array<{ grade: string; score: number; color: string }> = [
    { grade: 'A', score: 90, color: '#059669' },
    { grade: 'B', score: 80, color: '#22c55e' },
    { grade: 'C', score: 70, color: '#eab308' },
    { grade: 'D', score: 60, color: '#f97316' },
  ];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 140 }}>
      {/* Grade threshold lines */}
      {thresholds.map(t => (
        <g key={t.grade}>
          <line
            x1={padL} y1={yPos(t.score)}
            x2={width - padR} y2={yPos(t.score)}
            stroke={t.color} strokeWidth={1} strokeDasharray="4,3" opacity={0.5}
          />
          <text x={4} y={yPos(t.score) + 4} fontSize={9} fill={t.color} fontFamily="monospace">{t.grade}</text>
        </g>
      ))}
      {/* Line */}
      {sorted.length > 1 && (
        <polyline
          points={points}
          fill="none"
          stroke="#059669"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {/* Dots */}
      {sorted.map((s, i) => (
        <circle
          key={s.id}
          cx={xPos(i)} cy={yPos(s.total_score)}
          r={3}
          fill="#059669"
        />
      ))}
      {/* X axis date labels */}
      {sorted.map((s, i) => {
        if (sorted.length <= 4 || i % Math.ceil(sorted.length / 4) === 0) {
          return (
            <text
              key={`lbl-${s.id}`}
              x={xPos(i)} y={height - 4}
              fontSize={8}
              textAnchor="middle"
              fill="#9ca3af"
              fontFamily="sans-serif"
            >
              {s.computed_at.slice(5, 10)}
            </text>
          );
        }
        return null;
      })}
    </svg>
  );
}

export function SecurityCulturePage() {
  const { data: currentData, loading: currentLoading, error: currentError, refresh: refreshCurrent } =
    useApi<CurrentScoreResponse>('/protect/security-culture?action=current');
  const { data: historyData, refresh: refreshHistory } =
    useApi<HistoryResponse>('/protect/security-culture');
  const { data: certData, refresh: refreshCert } =
    useApi<CertificateResponse>('/protect/security-culture?action=certificate');
  const { data: leaderboardData } =
    useApi<LeaderboardResponse>('/protect/security-culture?action=leaderboard');

  const [computing, setComputing] = useState(false);
  const [computeError, setComputeError] = useState<string | null>(null);

  const score = currentData?.data;
  const history = historyData?.data ?? [];
  const certificate = certData?.data;
  const leaderboard = leaderboardData?.data;

  async function handleCompute() {
    setComputing(true);
    setComputeError(null);
    try {
      await post<ComputeResponse>('/protect/security-culture?action=compute');
      refreshCurrent();
      refreshHistory();
      refreshCert();
    } catch (err) {
      setComputeError(err instanceof Error ? err.message : 'Failed to compute score');
    } finally {
      setComputing(false);
    }
  }

  if (currentLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-400 text-sm">Loading security culture score…</div>
      </div>
    );
  }

  if (currentError) {
    return (
      <div className="p-6 text-red-600 text-sm">Error loading security culture score: {currentError}</div>
    );
  }

  const grade = score?.letter_grade ?? 'F';
  const gradeColor = GRADE_COLORS[grade];
  const gradeBg = GRADE_BG_COLORS[grade];
  const gradeRing = GRADE_RING[grade];

  const components: Array<{
    label: string;
    key: 'training_score' | 'phishing_score' | 'email_score' | 'proactive_score';
    details: string;
    color: string;
  }> = [
    {
      label: 'Training Culture',
      key: 'training_score',
      details: score
        ? `${pct(score.training_completion_rate)} complete · ${pct(score.training_overdue_rate)} overdue`
        : '—',
      color: 'bg-emerald-500',
    },
    {
      label: 'Phishing Resilience',
      key: 'phishing_score',
      details: score
        ? `${pct(score.phishing_click_rate)} click rate · ${pct(score.phishing_report_rate)} report rate`
        : '—',
      color: 'bg-blue-500',
    },
    {
      label: 'Email Security Posture',
      key: 'email_score',
      details: score
        ? `${pct(score.spf_dkim_pass_rate)} SPF/DKIM pass · ${score.blacklist_clean ? 'Clean' : 'Blacklisted'}`
        : '—',
      color: 'bg-violet-500',
    },
    {
      label: 'Proactive Security',
      key: 'proactive_score',
      details: score
        ? `${pct(score.dark_web_response_rate)} DW resolved · ${pct(score.posture_resolution_rate)} posture resolved`
        : '—',
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Culture Score</h1>
          <p className="text-sm text-gray-500 mt-1">
            A gamified, organization-wide measure of security behavior and posture.
          </p>
        </div>
        <button
          onClick={handleCompute}
          disabled={computing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {computing ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Computing…
            </>
          ) : (
            'Compute Now'
          )}
        </button>
      </div>

      {computeError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {computeError}
        </div>
      )}

      {/* Hero Score Section */}
      <div className={`rounded-2xl border-2 p-8 ${gradeBg}`}>
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Grade ring */}
          <div className={`flex-shrink-0 w-40 h-40 rounded-full ring-8 ${gradeRing} bg-white flex flex-col items-center justify-center shadow-lg`}>
            <span className={`text-7xl font-extrabold leading-none ${gradeColor}`}>{grade}</span>
            <span className="text-gray-500 text-sm font-medium mt-1">
              {score ? `${score.total_score}/100` : '—'}
            </span>
          </div>

          {/* Metadata */}
          <div className="flex-1 space-y-3 text-center md:text-left">
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              {/* Momentum badge */}
              {score && (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  score.momentum === 'improving'
                    ? 'bg-green-100 text-green-700'
                    : score.momentum === 'declining'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {score.momentum === 'improving' ? '↑' : score.momentum === 'declining' ? '↓' : '→'}
                  {' '}{score.momentum.charAt(0).toUpperCase() + score.momentum.slice(1)}
                </span>
              )}
              {/* Champion badge */}
              {score?.is_security_champion && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700 border border-yellow-300">
                  ★ Security Champion
                </span>
              )}
            </div>

            {score ? (
              <div className="space-y-1 text-sm text-gray-600">
                <p>
                  Period: {score.period_start.slice(0, 10)} — {score.period_end.slice(0, 10)}
                </p>
                {score.score_4w_ago !== undefined && (
                  <p>
                    4 weeks ago: <strong>{score.score_4w_ago}</strong>
                    {score.total_score !== score.score_4w_ago && (
                      <span className={score.total_score > score.score_4w_ago ? 'text-green-600' : 'text-red-600'}>
                        {' '}({score.total_score > score.score_4w_ago ? '+' : ''}{score.total_score - score.score_4w_ago})
                      </span>
                    )}
                  </p>
                )}
                {score.consecutive_improving_weeks > 0 && (
                  <p className="text-emerald-600 font-medium">
                    {score.consecutive_improving_weeks} consecutive week{score.consecutive_improving_weeks !== 1 ? 's' : ''} improving
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Last computed: {new Date(score.computed_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No score computed yet. Click "Compute Now" to generate your first score.</p>
            )}
          </div>
        </div>
      </div>

      {/* Score Breakdown — 4 component cards */}
      {score && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {components.map(c => (
            <div key={c.key} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{c.label}</div>
              <div className="text-2xl font-bold text-gray-900">
                {score[c.key]}
                <span className="text-sm font-normal text-gray-400">/25</span>
              </div>
              <ProgressBar value={score[c.key]} max={25} color={c.color} />
              <p className="text-xs text-gray-500 mt-2">{c.details}</p>
            </div>
          ))}
        </div>
      )}

      {/* Score History Chart */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Score History (last 12 weeks)</h2>
          <ScoreHistoryChart scores={history} />
          <div className="flex gap-4 mt-2 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-px bg-emerald-500" style={{ display: 'inline-block', height: 2 }} /> Your score
            </span>
            <span className="flex items-center gap-1 text-emerald-600">— A (90)</span>
            <span className="flex items-center gap-1 text-green-500">— B (80)</span>
            <span className="flex items-center gap-1 text-yellow-500">— C (70)</span>
            <span className="flex items-center gap-1 text-orange-500">— D (60)</span>
          </div>
        </div>
      )}

      {/* Certificate Section */}
      {certificate && !certificate.is_revoked && (
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-1">Security Culture Certificate</h2>
              <div className="flex items-center gap-3">
                <span className={`text-4xl font-extrabold ${GRADE_COLORS[certificate.letter_grade]}`}>
                  {certificate.letter_grade}
                </span>
                <div>
                  <p className="text-base font-bold text-gray-900">{certificate.org_name}</p>
                  <p className="text-xs text-gray-500">Score: {certificate.total_score}/100</p>
                  <p className="text-xs text-gray-500">
                    Valid through {certificate.valid_until.slice(0, 10)}
                  </p>
                  <p className="text-xs text-gray-400">#{certificate.certificate_number}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 max-w-md">
                Paste this certificate link into a QBR or client email to showcase your security maturity.
              </p>
            </div>
            <a
              href={`/api/protect/security-culture/${certificate.id}?action=certificate_html`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              Download Certificate
            </a>
          </div>
        </div>
      )}

      {/* Industry Benchmark */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Industry Benchmark</h2>
        {leaderboard ? (
          <div className="space-y-3">
            {[
              { label: 'SMB Average', score: leaderboard.smb_average, color: 'bg-gray-300' },
              { label: 'MSP-Managed Average', score: leaderboard.industry_average, color: 'bg-blue-400' },
              {
                label: 'Your Organization',
                score: leaderboard.your_score ?? 0,
                color: leaderboard.your_score != null && leaderboard.your_score >= 80
                  ? 'bg-emerald-500'
                  : leaderboard.your_score != null && leaderboard.your_score >= 60
                  ? 'bg-yellow-400'
                  : 'bg-red-400',
              },
            ].map(row => (
              <div key={row.label}>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>{row.label}</span>
                  <span className="font-semibold">{row.score}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${row.color}`}
                    style={{ width: `${row.score}%` }}
                  />
                </div>
              </div>
            ))}
            {leaderboard.percentile != null && (
              <p className="text-xs text-gray-500 mt-2">
                Your organization is in the <strong>{leaderboard.percentile}th percentile</strong> of MSP-managed clients.
              </p>
            )}
            <p className="text-xs text-gray-400">{leaderboard.note}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Compute a score to see how you compare.</p>
        )}
      </div>
    </div>
  );
}
